import { OrderSizeType } from '@/types/enums/order'

type AmountLike = {
  type?: string | null
  value?: number | null
} | null

interface NotionalOrderValueInput {
  /** The order's `amount` (total quantity + size type). */
  amount: AmountLike | undefined
  /** Filled share count (`filled_amount.value`), if any. */
  filledQty: number | null | undefined
  /** Realized average fill price (`filled_avg_price.pop`); null until fills. */
  avgFillPrice: number | null | undefined
  /** Live NBBO last price (`nbboLastPrice`). */
  lastPrice: number | null | undefined
  /** True when the order is terminal (Filled / Canceled / Rejected). */
  isTerminal: boolean
}

/**
 * Notional order value for the blotter "Notional value" column. Branches on
 * `amount.type`, and for shares on the order's fill state:
 *
 * - `notional` (mutual funds, dollar-denominated equity): the amount is already
 *   a USD notional — echoed unchanged regardless of fill state.
 * - `par` / unknown / nullish: fixed income has no equity notional — null.
 * - `shares`:
 *   - Terminal (Filled / Canceled / Rejected): value only the filled portion at
 *     the realized average fill price (`filledQty × avgFillPrice`). A terminal
 *     order with no fills (fully canceled / rejected) has no value → null.
 *   - Active with fills: the realized average price across the full order
 *     (`totalQty × avgFillPrice`).
 *   - Active with no fills yet: the live last-price estimate
 *     (`totalQty × lastPrice`).
 *
 * Returns the numeric sort/filter key (null renders as a placeholder). Money
 * formatting is applied by the column's valueFormatter.
 */
export function getNotionalOrderValue({
  amount,
  filledQty,
  avgFillPrice,
  lastPrice,
  isTerminal,
}: NotionalOrderValueInput): number | null {
  if (!amount || typeof amount.value !== 'number') return null
  if (amount.type === OrderSizeType.Notional) return amount.value
  if (amount.type !== OrderSizeType.Shares) return null

  const totalQty = amount.value

  // Terminal: value only what actually filled, at the realized average price.
  if (isTerminal) {
    if (
      typeof filledQty !== 'number' ||
      filledQty <= 0 ||
      typeof avgFillPrice !== 'number' ||
      avgFillPrice === 0
    ) {
      return null
    }
    return filledQty * avgFillPrice
  }

  // Active with fills: apply the realized average price across the full order.
  if (
    typeof filledQty === 'number' &&
    filledQty > 0 &&
    typeof avgFillPrice === 'number' &&
    avgFillPrice !== 0
  ) {
    return totalQty * avgFillPrice
  }

  // Active with no fills yet: estimate from the live last price.
  if (typeof lastPrice !== 'number' || lastPrice === 0) return null
  return totalQty * lastPrice
}
